import { z } from 'zod'

import {
  FaqSchema,
  LOCALES,
  LocalizedMediaListSchema,
  LocalizedMediaSchema,
  LocalizedSchema,
  PublishStatusSchema,
  SeoSchema,
  SlugSchema,
  paginated,
} from './common'
import { BUILTIN_TYPES, TEMPLATES, TypeNamesSchema } from './content-types'
import { PublicFaqSchema, PublicImageSchema, PublicSeoSchema } from './public'

// Sections a site owner adds themselves (issue #72, part B). Their entries are generic: title, summary, image,
// gallery, body, an optional price line, FAQs and SEO. A catalogue-style section has categories and items; the
// other templates have items only.

export const MAX_CUSTOM_TYPES = 10

/** URL segments a section can not take: built-in sections, fixed routes and the language prefixes. */
export const RESERVED_SLUGS: readonly string[] = [
  ...Object.values(BUILTIN_TYPES).map((t) => t.slug),
  'about',
  'contact',
  'locations',
  'home',
  'api',
  'media',
  'admin',
  'sitemap',
  'robots',
  'llms',
  // The online store's own pages.
  'cart',
  'checkout',
  'account',
  'wishlist',
  'order',
  'track',
  'privacy-notice',
  ...LOCALES,
]

export const CustomSlugSchema = SlugSchema.max(40).refine((s) => !RESERVED_SLUGS.includes(s) && !s.startsWith('_'), 'This address is reserved')

/** Add a section. The key is fixed for its lifetime; the address (slug) can change later. */
export const CreateContentTypeRequestSchema = z.object({
  slug: CustomSlugSchema,
  template: z.enum(TEMPLATES),
  names: TypeNamesSchema,
})
export type CreateContentTypeRequest = z.infer<typeof CreateContentTypeRequestSchema>

export const CustomEntryKindSchema = z.enum(['category', 'item'])
export type CustomEntryKind = z.infer<typeof CustomEntryKindSchema>

export const CustomEntryInputSchema = z.object({
  slug: SlugSchema,
  /** The category of an item in a catalogue section (empty otherwise). */
  categoryId: z.string().min(1).max(64).nullable().default(null),
  sortOrder: z.number().int().min(0).max(10_000).default(0),
  status: PublishStatusSchema,
  title: LocalizedSchema,
  summary: LocalizedSchema.default({}),
  heroImage: LocalizedMediaSchema.default({}),
  gallery: LocalizedMediaListSchema.default({}),
  bodyHtml: LocalizedSchema.default({}),
  /** A free price line such as "From Rp 150.000". */
  price: LocalizedSchema.default({}),
  faqs: z.array(FaqSchema).max(40).default([]),
  /** When it went (or goes) live; used for the date shown on article-style sections. */
  publishedAt: z.string().nullable().default(null),
  seo: SeoSchema,
})
export type CustomEntryInput = z.infer<typeof CustomEntryInputSchema>

export const CustomEntrySchema = CustomEntryInputSchema.extend({
  id: z.string(),
  typeKey: z.string(),
  kind: CustomEntryKindSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
})
export type CustomEntry = z.infer<typeof CustomEntrySchema>

export const emptyCustomEntry = (): CustomEntryInput => ({
  slug: '',
  categoryId: null,
  sortOrder: 0,
  status: 'draft',
  title: {},
  summary: {},
  heroImage: {},
  gallery: {},
  bodyHtml: {},
  price: {},
  faqs: [],
  publishedAt: null,
  seo: { title: {}, description: {}, ogImage: {}, canonicalPath: null, noindex: false },
})

// ── Public ───────────────────────────────────────────────────────────────

export const PublicCustomCardSchema = z.object({
  slug: z.string(),
  /** Slug of the category (items of a catalogue section) or empty. */
  categorySlug: z.string(),
  title: z.string(),
  summary: z.string(),
  price: z.string(),
  image: PublicImageSchema.nullable(),
  publishedAt: z.string().nullable(),
})
export type PublicCustomCard = z.infer<typeof PublicCustomCardSchema>

export const PublicCustomEntrySchema = PublicCustomCardSchema.extend({
  kind: CustomEntryKindSchema,
  gallery: z.array(PublicImageSchema),
  bodyHtml: z.string(),
  faqs: z.array(PublicFaqSchema),
  seo: PublicSeoSchema,
  updatedAt: z.string(),
  /** The category's own title (items) and, for a category, its items. */
  categoryTitle: z.string(),
  items: z.array(PublicCustomCardSchema),
})
export type PublicCustomEntry = z.infer<typeof PublicCustomEntrySchema>

/** A section's landing page: its categories (catalogue) and items. */
export const PublicCustomIndexSchema = z.object({
  categories: z.array(PublicCustomCardSchema),
  items: z.array(PublicCustomCardSchema),
})
export type PublicCustomIndex = z.infer<typeof PublicCustomIndexSchema>

/** Old address → current address, for sections whose address changed. */
export const PublicRedirectsSchema = z.object({ items: z.array(z.object({ from: z.string(), to: z.string() })) })

export const CustomEntryListSchema = paginated(
  z.object({
    id: z.string(),
    slug: z.string(),
    title: LocalizedSchema,
    status: PublishStatusSchema,
    kind: CustomEntryKindSchema,
    categoryId: z.string().nullable(),
    updatedAt: z.string(),
  }),
)
